import React, { useEffect, useState } from "react";
import { getUsersByOffer, UserPurchase } from "../services/offreService";

interface OfferPurchasesListProps {
    offerId: number;
}
interface UserPurchaseWithInvoice extends UserPurchase {
    invoiceUrl?: string;
}

const OfferPurchasesList: React.FC<OfferPurchasesListProps> = ({ offerId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [sortConfig, setSortConfig] = useState<{
        key: keyof UserPurchase | "user.email" | "offer.name" | "purchasedAt";
        direction: "asc" | "desc";
    }>({ key: "purchasedAt", direction: "desc" });
    const [purchases, setPurchases] = useState<UserPurchaseWithInvoice[]>([]);


    useEffect(() => {
        const fetchPurchases = async () => {
            try {
                setLoading(true);
                const data = await getUsersByOffer(offerId);

            const purchasesWithInvoice: UserPurchaseWithInvoice[] = data.map((purchase) => ({
            ...purchase,
            invoiceUrl: purchase.invoiceUrl
            }));


                console.log("Fetched purchases with invoice URLs:", purchasesWithInvoice);

                setPurchases(purchasesWithInvoice);
            } catch (err: any) {
                setError(err.message || "Erreur lors de la récupération des utilisateurs.");
            } finally {
                setLoading(false);
            }
        };

        fetchPurchases();
    }, [offerId]);


    const handleSort = (key: typeof sortConfig.key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
        });
    };

    const formatDate = (dateString: string | Date) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return "Date invalide";
            }
            return date.toLocaleDateString("fr-FR");
        } catch (error) {
            return "Date invalide";
        }
    };

    const formatTime = (dateString: string | Date) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return "--:--";
            }
            return date.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit"
            });
        } catch (error) {
            return "--:--";
        }
    };

    const filteredAndSortedPurchases = React.useMemo(() => {
        let filtered = purchases.filter(purchase =>
            purchase.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchase.offer.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        filtered.sort((a, b) => {
            let aValue: any, bValue: any;

            switch (sortConfig.key) {
                case "user.email":
                    aValue = a.user.email;
                    bValue = b.user.email;
                    break;
                case "offer.name":
                    aValue = a.offer.name;
                    bValue = b.offer.name;
                    break;
                case "purchasedAt":
                    aValue = new Date(a.purchasedAt);
                    bValue = new Date(b.purchasedAt);
                    break;
                default:
                    aValue = a[sortConfig.key as keyof UserPurchase];
                    bValue = b[sortConfig.key as keyof UserPurchase];
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [purchases, searchTerm, sortConfig]);



    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
                <span className="ms-3">Chargement des utilisateurs...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
                <div className="flex-shrink-0 me-3">
                    <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                </div>
                <div>{error}</div>
            </div>
        );
    }

    if (purchases.length === 0) {
        return (
            <div className="text-center py-5">
                <div className="mb-3">
                    <svg width="64" height="64" fill="#6c757d" viewBox="0 0 16 16">
                        <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                        <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z" />
                    </svg>
                </div>
                <h4 className="text-muted">Aucun utilisateur n'a acheté cette offre</h4>
                <p className="text-muted">Les utilisateurs apparaîtront ici après avoir acheté cette offre.</p>
            </div>
        );
    }

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">

                    <div className="w-100 w-md-auto">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light border-end-0">
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                className="form-control form-control-sm border-start-0"
                                placeholder="Rechercher un utilisateur ou offre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body p-0">
                <div className="table-responsive" style={{
                    maxHeight: filteredAndSortedPurchases.length > 6 ? "500px" : "auto",
                    overflowY: filteredAndSortedPurchases.length > 6 ? "auto" : "visible",
                    overflowX: "auto"
                }}>
                    <table className="table table-hover mb-0">
                        <thead style={{ backgroundColor: "#4BB0C0", color: "white" }}>
                            <tr>
                                <th
                                    className="py-3 px-4 cursor-pointer"
                                    onClick={() => handleSort("user.email")}
                                    style={{ minWidth: "250px" }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <span>Client</span>
                                    </div>
                                </th>
                                <th
                                    className="py-3 px-4 text-center cursor-pointer"
                                    style={{}}
                                >
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                        <span>Quota journalier</span>
                                    </div>
                                </th>
                                <th
                                    className="py-3 px-4 text-center cursor-pointer"
                                    style={{}}
                                >
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                        <span>Quota mensuel</span>
                                    </div>
                                </th>
                                <th
                                    className="py-3 px-4 text-center"
                                    style={{}}
                                >
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                        <span>Quota annuel</span>
                                    </div>
                                </th>

                                <th
                                    className="py-3 px-4 cursor-pointer"
                                    onClick={() => handleSort("offer.name")}
                                    style={{ minWidth: "180px" }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <span>Offre</span>
                                    </div>
                                </th>
                                <th
                                    className="py-3 px-4 cursor-pointer"
                                    onClick={() => handleSort("purchasedAt")}
                                    style={{ minWidth: "180px" }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <span>Date d'achat</span>
                                    </div>
                                </th>
                                 <th className="py-3 px-4 text-center">
                                    <span>Facture</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredAndSortedPurchases.map((purchase, index) => (
                                <tr key={purchase.user.id} className={index % 2 === 0 ? "bg-white" : "bg-light"}>
                                    <td className="py-3 px-4">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: "40px", height: "40px" }}>
                                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                                                    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                                                    <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="fw-medium">{purchase.user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <div className="d-inline-block">
                                            <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-3 py-2">
                                                {purchase.offer.daily_emails}
                                                <small className="ms-1 text-muted">/jour</small>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <div className="d-inline-block">
                                            <span
                                                className="badge rounded-pill px-3 py-2"
                                                style={{
                                                    backgroundColor: "rgba(75, 176, 192, 0.1)",
                                                    color: "#4BB0C0",
                                                    border: "1px solid rgba(75, 176, 192, 0.25)",
                                                }}
                                            >
                                                {purchase.offer.monthly_emails}
                                                <small className="ms-1 text-muted">/mois</small>
                                            </span>
                                        </div>
                                    </td>

                                    <td className="py-3 px-4 text-center">
                                        <div className="d-inline-block">
                                            <span
                                                className="badge rounded-pill px-3 py-2"
                                                style={{
                                                    backgroundColor: "rgba(75, 176, 192, 0.1)",
                                                    color: "#4BB0C0",
                                                    border: "1px solid rgba(75, 176, 192, 0.25)",
                                                }}
                                            >
                                                {purchase.offer.yearly_emails}
                                                <small className="ms-1 text-muted">/an</small>
                                            </span>
                                        </div>
                                    </td>

                                    <td className="py-3 px-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                className="rounded px-2 py-1 small"
                                                style={{
                                                    backgroundColor: "rgba(244, 162, 97, 0.1)",
                                                    color: "#F4A261",
                                                }}
                                            >
                                                {purchase.offer.name}
                                            </div>
                                            {/* <span className="text-muted small">#{purchase.offer.id}</span */}
                                        </div>
                                    </td>

                                    <td className="py-3 px-4">
                                        <div>
                                            <div className="fw-medium">
                                                {formatDate(purchase.purchasedAt)}
                                            </div>
                                            <small className="text-muted">
                                                {formatTime(purchase.purchasedAt)}
                                            </small>
                                        </div>
                                    </td> 
                                    <td className="py-3 px-4 text-center">
                                    {purchase.invoiceUrl ? (
                                        <a
                                            href={purchase.invoiceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm  d-flex align-items-center justify-content-center"
                                            style={{ 
                                                color: 'white',
                                                borderRadius: '6px',
                                                fontWeight: 600,
                                                backgroundColor: '#4bb0c0',
                                                gap: "5px",
                                                border: "1px solid #4bb0c0",
                                             }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                <path d="M.5 9.9a.5.5 0 0 1 .5-.5H5V1.5a.5.5 0 0 1 1 0V9.4h4a.5.5 0 0 1 .5.5v.6a.5.5 0 0 1-.5.5H6.5v5a.5.5 0 0 1-1 0v-5H1a.5.5 0 0 1-.5-.5v-.6z"/>
                                            </svg>
                                            <span>Voir la facture</span>
                                        </a>
                                    ) : (
                                        <span className="text-muted small">Aucune facture</span>
                                    )}
                                </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card-footer bg-white border-0 py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="small text-muted">
                        Affichage de {filteredAndSortedPurchases.length} sur {purchases.length} utilisateurs
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfferPurchasesList;